import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
// import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom'
import { DisplayCard, AllPayments, userHasCardOnFile, getAwnPlusAppPrice, getAwnTeamsAppPrice, showAwnFreeTrial, isPlus, plusSubHasCode, subIsTeams, subIsMonthly, getAppSubscriptionId, getSubscriptionConfig, triggerNativeIAP, subIsIap, createSubscriptionPriceGetter, doAppVersionRedirect, isCameraSubscription, isCellularSubscription, isHeatStressSubscription, getSubscriptionDisplayPrice, getSubscriptionDisplayName, subIsPlus } from './'
import { findExistingSubscription, detectDowngrade, TIER_HIERARCHIES } from './subscriptionHelpers'
import { Loader } from '../../components'
import classnames from 'classnames'
import { path } from 'ramda'
import { appIsGteVersion, isAndroidApp, isApp, isIosApp } from '../../common/ambient'
import { billingSubscribe, billingApplyPromoCode, checkCode } from './billingAccess'
import { useAddUserAction } from '../social/redux/addUserAction'
import bindDeviceActions from '../device/redux/bindDeviceActions'

const isGiftCodeProcess = (codeValidity) => {
  if (isApp() && codeValidity !== true) {
    return false
  }
  return true
}

// Determines if we should use Stripe flow instead of native IAP
const shouldUseStripeFlow = (user, codeValidity, paymentMethod) => {
  // Always use Stripe for gift codes
  if (isGiftCodeProcess(codeValidity)) {
    return true
  }
  
  // If payment method is explicitly set to stripe
  if (paymentMethod === 'stripe') {
    return true
  }
  
  // If user has existing subscription and it's a Stripe subscription (not IAP)
  if (user.plus && !subIsIap(user.plus)) {
    return true
  }
  
  // Default to IAP for mobile apps if no other conditions met
  return false
}

function SubscribeProcess ({ user, subscription, children, device, actions, userActions, code = '', plusUserSwitch, setSubscription, redeemCode = false, onSubscriptionChange, useDiscountedPricing = false, forceIAP = false, paymentMethod = 'auto', startPolling, currentDevice, macAddress, canShowExternalPurchaseLinks = true }) {
  // Note: 'actions' prop is deviceActions provided by bindDeviceActions HOC
  const [hasPaymentMethod, setHasPaymentMethod] = useState(!!userHasCardOnFile(user))
  const [initialCardCheck, setInitialCardCheck] = useState(true)
  const [cardSetupReady, setCardSetupReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [trialLimitError, setTrialLimitError] = useState(null)
  const [_code, setCode] = useState(code)
  const [_plusUserSwitch, setPlusUserSwitch] = useState(plusUserSwitch)
  const [codeValidity, setCodeValidity] = useState(null)
  const [codeProductName, setCodeProductName] = useState(null)
  const [showCodeInput, setShowCodeInput] = useState(redeemCode || !!code)
  const [previewData, setPreviewData] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const expectedStateRef = useRef({ codeValidity: null, codeProductName: null, _code: '', subscription: '' })
  const { addUserAction } = useAddUserAction()
  const history = useHistory()

  // Extract stable user properties to prevent unnecessary re-renders
  const userId = useMemo(() => path(['info', '_id'], user), [user])
  const hasUsedDiscount = useMemo(() =>
    path(['info', 'payments', 'nativeAppDiscountWasUsed'], user) === true,
    [user]
  )
  const userPayments = useMemo(() =>
    path(['info', 'payments'], user),
    [user]
  )
  const userPlus = useMemo(() => user.plus, [user])

  const isPlusUser = useMemo(() => !!userPlus, [userPlus])

  // Check if user has already used the native app discount
  const canUseDiscount = useMemo(() =>
    useDiscountedPricing && !hasUsedDiscount,
    [useDiscountedPricing, hasUsedDiscount]
  )

  // Clear gift code state when code prop is removed (e.g., browser back navigation)
  useEffect(() => {
    if (!code) {
      setCode('')
      setCodeValidity(null)
      setCodeProductName(null)
      setShowCodeInput(redeemCode || false)
      setPreviewData(null) // Clear stale preview data to prevent showing $0 while new preview loads
    } else {
      setCode(code)
    }
  }, [code, redeemCode])

  // Update hasPaymentMethod when user payment data changes
  useEffect(() => {
    setHasPaymentMethod(!!userPayments?.defaultPaymentMethod)
    setInitialCardCheck(false)
  }, [userPayments])

  // Parse subscription type from product name
  const annual = /yearly/.test(subscription)
  const isTeams = /for-teams/.test(subscription)
  const isCameraSub = isCameraSubscription(subscription)
  const isCellSub = isCellularSubscription(subscription)
  const isHeatStressSub = isHeatStressSubscription(subscription)

  // Find existing subscription for upgrade/downgrade/prorating logic
  const existingSubscription = useMemo(() => {
    const subscriptions = userPayments?.subscriptions || []
    if (macAddress) {
      // Camera subscription
      return findExistingSubscription({ info: { payments: userPayments } }, macAddress, isCameraSubscription)
    } else if (isHeatStressSub) {
      // Heat Stress subscription
      return findExistingSubscription({ info: { payments: userPayments } }, null, isHeatStressSubscription)
    } else {
      // AWN+ subscription
      return findExistingSubscription({ info: { payments: userPayments } }, null, (prodName) => {
        const sub = subscriptions.find(s => s.productName === prodName)
        return sub ? subIsPlus(sub) : false
      })
    }
  }, [userPayments, macAddress, isHeatStressSub])

  // Determine if this is a downgrade using universal helper
  const isDowngrade = useMemo(() => {
    if (!existingSubscription || existingSubscription.productName === subscription) {
      return false
    }
    if (macAddress) {
      return detectDowngrade(TIER_HIERARCHIES.camera, existingSubscription, subscription)
    } else if (isHeatStressSub) {
      return detectDowngrade(TIER_HIERARCHIES.heatStress, existingSubscription, subscription)
    } else if (plusUserSwitch) {
      return detectDowngrade(TIER_HIERARCHIES.awnPlus, existingSubscription, subscription)
    }
    return false
  }, [existingSubscription, subscription, macAddress, isHeatStressSub, plusUserSwitch])

  // Unified subscription request handler for both preview and purchase
  const performSubscriptionRequest = useCallback(async (isPreview = false) => {
    if (!userId) return
    let productName = subscription

    // Use code's product name if we have a valid code
    if (codeValidity === true && codeProductName) {
      productName = codeProductName
    }

    // Case 1: Existing Plus user applying promo code to same subscription
    if (userPlus && codeValidity === true) {
      return await billingApplyPromoCode(userId, _code, isPreview)
    }

    // Case 2: All other cases (new sub, upgrade, downgrade, no code)
    const billingSubOpts = { isPreview }

    if (canUseDiscount) {
      billingSubOpts.discountPercent = 20
    }

    // For upgrades/downgrades
    if (existingSubscription && existingSubscription.productName !== productName) {
      billingSubOpts.body = { new_product_name: productName }
      productName = existingSubscription.productName
      billingSubOpts.method = 'PUT'
    }

    if (codeValidity === true) {
      billingSubOpts.promoCode = _code
    }

    if (isApp()) {
      billingSubOpts.headers = { 'x-platform': isIosApp() ? 'ios' : 'android' }
    }

    return await billingSubscribe(userId, macAddress || null, productName, billingSubOpts)
  }, [userId, userPlus, subscription, codeValidity, codeProductName, _code, canUseDiscount, existingSubscription, macAddress])

  // Fetch preview data on load
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const fetchPreview = async () => {
        if (!userId || !subscription) return
        if (loading) return // Skip preview during purchase/polling

        // For Stripe flows, wait for card-setup to complete if user has no card
        // This ensures card-setup API call finishes before preview API call (billing server requirement)
        if (!useIapFlow && !hasPaymentMethod && !cardSetupReady) {
          return // Wait for card-setup to complete
        }

        // Clear error when conditions change (React won't re-render if value doesn't change)
        setTrialLimitError(null)

        // Update expected state ref and capture the state used for this request
        const requestState = { codeValidity, codeProductName, _code, subscription }
        expectedStateRef.current = requestState
        setPreviewLoading(true)

        // Skip preview for downgrades
        if (isDowngrade) {
          setPreviewLoading(false)
          return
        }

        try {
          const result = await performSubscriptionRequest(true)
          const currentExpected = expectedStateRef.current

          // Check for trial limit error
          if (!result.success && result.message && result.message.includes('trial will exceed 720 days')) {
            setTrialLimitError(result.message)
            setPreviewData(null)
            setPreviewLoading(false)
            return
          }

          // Clear any previous trial error on success
          setTrialLimitError(null)

          // Only update if the code state hasn't changed since we started the request
          // This prevents stale responses (with old gift code) from overwriting newer responses
          if (requestState.codeValidity === currentExpected.codeValidity &&
              requestState.codeProductName === currentExpected.codeProductName &&
              requestState._code === currentExpected._code &&
              requestState.subscription === currentExpected.subscription) {
            setPreviewData(result)
          }
        } catch (error) {
          console.error('Error fetching preview:', error)
          // Don't set trial error for network/unexpected errors
        } finally {
          setPreviewLoading(false)
        }
      }

      fetchPreview()
    }, 100) // 100ms debounce

    return () => clearTimeout(timeoutId)
  }, [userId, subscription, macAddress, codeValidity, codeProductName, _code, canUseDiscount, isDowngrade, loading, performSubscriptionRequest, useIapFlow, hasPaymentMethod, cardSetupReady])

  // Consolidated logic for determining payment flow
  const determineUseStripeFlow = useCallback(() => {
    // Force IAP if explicitly requested
    if (forceIAP) {
      return false
    }

    // Always use Stripe for device subscriptions (cameras, cellular)
    if (macAddress || isCameraSub || isCellSub) {
      return true
    }

    // Always use Stripe for discounted pricing
    if (canUseDiscount) {
      return true
    }

    // Use the existing shouldUseStripeFlow logic
    return shouldUseStripeFlow({ plus: userPlus }, codeValidity, paymentMethod)
  }, [userPlus, codeValidity, paymentMethod, canUseDiscount, forceIAP, macAddress, isCameraSub, isCellSub])
  
  // Auto-show gift code input when redeemCode is true or code is provided
  useEffect(() => {
    if (redeemCode || code) {
      setShowCodeInput(true)
    }
  }, [redeemCode, code])
  useEffect(() => {
    if (device.appPurchaseFlowCanceled && loading) {
      setLoading(false)
      actions.setThing('appPurchaseFlowCanceled', false)
    }
  }, [device, loading, setLoading, actions])

  // Validate code when passed as prop (from URL parameter)
  useEffect(() => {
    const validateCodeProp = async () => {
      // Only validate if:
      // 1. Code is provided
      // 2. Code validity hasn't been set yet (avoid re-validation)
      // 3. redeemCode flag is true (indicates this is a gift code flow)
      if (!_code || codeValidity !== null || !redeemCode) {
        return
      }

      try {
        const res = await checkCode(_code)
        const valid = res && res.valid === true

        if (valid) {
          // Check Teams downgrade scenario (same logic as GiftCodeForm)
          const isTeamsUser = userPlus && subIsTeams(userPlus)
          const isRegularAwnPlusCode = res.productName && /^awn-plus-account-/.test(res.productName)

          if (isTeamsUser && isRegularAwnPlusCode) {
            // Invalid: can't apply regular AWN+ code to Teams subscription
            setCodeValidity(false)
            setCode('') // Clear the invalid code
            return
          }

          // Valid code - set state
          setCodeValidity(true)
          setCodeProductName(res.productName)

          // Auto-switch subscription tier if needed
          if (res.productName && setSubscription) {
            const currentIsTeams = /for-teams/.test(subscription)
            const codeIsTeams = /for-teams/.test(res.productName)

            if (currentIsTeams !== codeIsTeams) {
              setSubscription(res.productName)
              if (onSubscriptionChange) {
                onSubscriptionChange(res.productName)
              }
            }
          }
        } else {
          // Invalid code
          setCodeValidity(false)
          setCode('') // Clear the invalid code
        }
      } catch (err) {
        console.error('Error validating code prop:', err)
        setCodeValidity(false)
        setCode('') // Clear on error
      }
    }

    validateCodeProp()
  }, [_code, codeValidity, redeemCode, userPlus, subscription, setSubscription, onSubscriptionChange])

  const appSubscriptionId = getAppSubscriptionId(subscription)

  const useIapFlow = useMemo(() =>
    (isAndroidApp() || isIosApp()) && !determineUseStripeFlow()
  , [determineUseStripeFlow])

  const shouldShowCardMessage = !useIapFlow && !hasPaymentMethod

  // btnBuyHandler ---------------------------------
  const btnBuyHandler = useCallback(async () => {
    setLoading(true)
    actions.setThing('appPurchaseFlowCanceled', false)
    if (typeof window.saveAppleSubscriptionPricingInfo === 'undefined') {
      window.saveAppleSubscriptionPricingInfo = (data) => {
        addUserAction({
          data,
          // we need "to" for the index
          to: {
            type: 'user',
            _id: userId
          },
          type: 'iap-subscription'
        })
      }
    }
    // Use consolidated logic for determining payment flow
    const useStripeFlow = determineUseStripeFlow()

    // Native IAP flow
    if ((isIosApp() || isAndroidApp()) && !useStripeFlow) {
      if (doAppVersionRedirect(subscription)) {
        setLoading(true)
        actions.setThing('appPurchaseFlowCanceled', true)
        return
      }

      const success = triggerNativeIAP(subscription, userId)
      if (!success) {
        setLoading(false)
        // Could show error message or fallback to CC flow
        return
      }
    // stripe flow (not ios or android native app, or forced CC from app)
    } else {
      const result = await performSubscriptionRequest(false)

      if (!result.success) {
        // Check for trial limit error
        if (result.message && result.message.includes('trial will exceed 720 days')) {
          setTrialLimitError(result.message)
        }
        setLoading(false)
        return
      }

      // Start polling for payment completion
      if (startPolling) {
        startPolling()
      }
    }
  }, [userId, subscription, actions, addUserAction, determineUseStripeFlow, startPolling, performSubscriptionRequest])
  // btnBuyHandler ---------------------------------

  // Stable callbacks for AllPayments to prevent excessive re-renders
  const handleCardSuccess = useCallback(() => {
    setHasPaymentMethod(true)
  }, [])

  const handleCardSetupReady = useCallback(() => {
    setCardSetupReady(true)
  }, [])

  // STREAMLINED PROCESS REMOVED - all users now see the $0.00 step
  // const streamlinedCodeProcess = isPlusUser && !_plusUserSwitch && hasPaymentMethod
  const streamlinedCodeProcess = false // Disabled for now
  // useEffect(() => {
  //   console.log('SubscribeProcess streamlined check:', { streamlinedCodeProcess, codeValidity })
  //   if (streamlinedCodeProcess && codeValidity === true) {
  //     console.log('SubscribeProcess: Auto-calling btnBuyHandler due to streamlined process')
  //     btnBuyHandler()
  //   }
  // }, [codeValidity, streamlinedCodeProcess, btnBuyHandler])


  const subscriptionConfig = getSubscriptionConfig(subscription)
  const getPricing = createSubscriptionPriceGetter(device)

  // Get base price using the universal helper
  let price = getSubscriptionDisplayPrice(subscription)

  // Use preview data if available
  let displayPrice = price
  let displayTax = 0

  if (previewData && previewData.amountDue !== undefined) {
    // Preview data: just the amount, we'll add period separately
    displayPrice = `$${(previewData.amountDue / 100).toFixed(2)}`
    displayTax = previewData.tax || 0
  }

  const btnDisabled = useMemo(() => {
    let disabled = loading || previewLoading || !!trialLimitError
    if (!useIapFlow) {
      disabled = disabled || !hasPaymentMethod || initialCardCheck
    }
    return disabled
  }, [loading, previewLoading, useIapFlow, hasPaymentMethod, initialCardCheck, trialLimitError])

  if (isApp()) {
    // Show discounted price if using discounted CC pricing
    if (canUseDiscount && subscriptionConfig && subscriptionConfig.price) {
      const regularPrice = subscriptionConfig.price
      const discountedPrice = (regularPrice * 0.8).toFixed(2)
      price = `$${discountedPrice}`
    } else if (!isCameraSub) {
      // Only use getPricing for AWN+ subscriptions (not cameras)
      const pricingInfo = getPricing(isTeams ? 'teams' : 'plus', annual, !isGiftCodeProcess(codeValidity))
      price = pricingInfo.price.split('/')[0] // Remove /month or /year
    }
    // Camera subscriptions keep their calculated price from getSubscriptionDisplayPrice
  }
  let btnText = isDowngrade ? 'Downgrade' : 'Upgrade'
  let codeValid = false
  let extraText = ''

  // Calculate original price for price breakdown
  // Use standardized price field (always a number) if available, fallback to old parsing logic
  let originalPrice = 0
  if (subscriptionConfig) {
    originalPrice = subscriptionConfig.price
  }
  const hasFreeTrial = showAwnFreeTrial(user)
  let discountAmount = 0

  if (codeValidity === true) {
    btnText = 'Finish'
    price = '$0.00'
    discountAmount = originalPrice // Full price discount for gift code

    codeValid = true
    if (shouldShowCardMessage) {
      extraText = 'To activate your gift subscription, please register your card first.'
    }
  } else if (canUseDiscount && subscriptionConfig) {
    // Calculate 20% discount amount for app CC discount
    discountAmount = (originalPrice * 0.20).toFixed(2)
  }

  // Calculate prorating message from preview data
  let proRateMessage = ''
  if (previewData && previewData.grantedProratedSeconds > 0 && existingSubscription) {
    const proratedDays = Math.floor(previewData.grantedProratedSeconds / 86400)
    const currentPlanName = getSubscriptionDisplayName(existingSubscription)
    proRateMessage = (
      <p className='prorate'>
        {proratedDays} days will be added from your {currentPlanName} plan
      </p>
    )
  }

  // Calculate subscription display label with billing term
  let subscriptionDisplayLabel = 'Subscription' // fallback
  if (subscriptionConfig) {
    if (isCameraSub || isCellSub) {
      // Camera and cellular subscriptions already include term in label
      subscriptionDisplayLabel = subscriptionConfig.label
    } else {
      // AWN+ and Heat Stress subscriptions need term appended
      subscriptionDisplayLabel = `${subscriptionConfig.displayName} ${annual ? 'Annual' : 'Monthly'}`
    }
  }

  let show = ''
  if (userId) {
    show = (
      <>
        {!useIapFlow && (
          <>
            <DisplayCard />
            <AllPayments
              userId={userId}
              deviceId={currentDevice?._id}
              mode='file'
              onSuccess={handleCardSuccess}
              onSecretReady={handleCardSetupReady}
            />
          </>
        )}
        {/* Line-item breakdown - always show when preview data is available */}
        {previewData && (
          <div className='price-breakdown'>
            <div className='line-item'>
              <span className='label'>{subscriptionDisplayLabel}</span>
              <span className='amount'>${originalPrice.toFixed(2)}/{annual ? 'year' : 'month'}</span>
            </div>

            {/* Gift code line item - only show for AWN+ subscriptions */}
            {canShowExternalPurchaseLinks && !isCameraSub && !isCellSub && !isHeatStressSub && !plusSubHasCode(user) && (
              <div className={classnames('line-item gift-code', { 'gift-code-applied': codeValid })}>
                <span className='label'>
                  <span className='label-text'>{codeValid ? '1 year free gift code applied' : 'Gift code'}</span>
                  {codeValid ? (
                    <span className='code-applied'>Code: {_code}</span>
                  ) : annual ? (
                    <a
                      className='have-code-link'
                      onClick={() => userActions.doModal({ type: 'gift-code' })}
                    >
                      Enter gift code
                    </a>
                  ) : (
                    <span className='switch-annual-msg'>
                      <a onClick={() => {
                        if (setSubscription) {
                          // PlusSubscriptionModal path - update state
                          setSubscription(subscription.replace('monthly', 'yearly'))
                          setPlusUserSwitch(false)
                        } else {
                          // CheckoutPage path - navigate to new URL with yearly subscription
                          const yearlySubscription = subscription.replace('monthly', 'yearly')
                          const currentParams = new URLSearchParams(window.location.search)
                          currentParams.set('subscription', yearlySubscription)
                          currentParams.set('redeemCode', 'true')
                          history.push(`/subscriptions/checkout?${currentParams.toString()}`)
                        }
                      }}>Enter gift code</a>
                    </span>
                  )}
                </span>
                <span className='amount'>
                  {codeValid ? `-$${originalPrice.toFixed(2)}` : '$0'}
                </span>
              </div>
            )}

            {canUseDiscount && !codeValid && (
              <div className='line-item discount'>
                <span className='label'>20% First Cycle Discount</span>
                <span className='amount'>
                  -${discountAmount}
                  {hasFreeTrial && (
                    <span>(applied after trial)</span>
                  )}
                </span>
              </div>
            )}

            {hasFreeTrial && !codeValid && (
              <div className='line-item discount'>
                <span className='label'>First Month Free Trial</span>
                <span className='amount'>$0 <span>(first month)</span></span>
              </div>
            )}


            {/* Hide tax when gift code is applied */}
            {!codeValid && (
              <div className='line-item'>
                <span className='label'>Tax</span>
                <span className={`amount ${shouldShowCardMessage ? 'register-card-message' : ''}`}>
                  {shouldShowCardMessage ? 'Register a card to calculate' : `$${(displayTax / 100).toFixed(2)}`}
                </span>
              </div>
            )}
          </div>
        )}
        <div className='cart'>
          <div className='total-label'>
            Total
          </div>
          <div className='price'>
            <div className={shouldShowCardMessage ? 'register-card-message' : ''}>
              {shouldShowCardMessage ? 'Register a card to calculate' : displayPrice}
            </div>
          </div>
        </div>
        {proRateMessage}
        {trialLimitError && (
          <div className='trial-limit-error'>
            <p className='error'>
              {trialLimitError}
            </p>
          </div>
        )}
        <div className='buyBtn'>
          <button
            className='btn btn-primary buy-plan'
            disabled={btnDisabled}
            onClick={btnBuyHandler}
          >
            {btnText}
          </button>
          {extraText && <p><b>{extraText}</b></p>}
          {hasFreeTrial && canUseDiscount && !codeValid && (
            <p className='trial-discount-msg'>
              Start with a free month trial, then enjoy 20% off your first paid billing cycle!
            </p>
          )}
          {loading ? <><br /><p>Please wait</p><Loader /></> : ''}
          {children}
        </div>
      </>
    )
  }
  return (
    <div className={classnames('subscribe-process', { plus: streamlinedCodeProcess })}>
      {show}
    </div>
  )
}

SubscribeProcess.propTypes = {}
SubscribeProcess.defaultProps = {}

SubscribeProcess.displayName = 'SubscribeProcess'

export default bindDeviceActions(SubscribeProcess)
